import React, { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, Input, Label } from "@/components/ui-kit";
import { api, errorText } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";
import { fmtDay } from "@/lib/time";

export type AccountAction = "approve" | "reject" | "suspend" | "reactivate" | "fee";

const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Baghdad" });
const addDays = (ymd: string, n: number) => new Date(Date.parse(`${ymd}T12:00:00Z`) + n * 864e5).toISOString().slice(0, 10);

export const AccountActionDialog = ({ accountId, action, onClose, onDone }: { accountId: string; action: AccountAction | null; onClose: () => void; onDone: () => void }) => {
  const { t, lang } = usePrefs();
  const [reason, setReason] = useState("");
  const [fee, setFee] = useState({ model: "per_booking", amount: "", currency: "IQD", days: "" });
  const [busy, setBusy] = useState(false);
  if (!action) return null;
  const withFee = action === "approve" || action === "fee";
  const needsReason = action === "reject" || action === "suspend" || action === "reactivate";
  const trial = fee.model === "free_trial";

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    const feeBody = trial ? { model: fee.model, currency: fee.currency, trial_days: Number(fee.days) } : { model: fee.model, amount: fee.amount, currency: fee.currency };
    try {
      if (action === "fee") await api.post(`/admin/venue-accounts/${accountId}/fees`, feeBody);
      else await api.post(`/admin/venue-accounts/${accountId}/${action}`, action === "approve" ? { reason, fee: feeBody } : { reason });
      toast.success(t(action === "fee" ? "admin.feeSaved" : "admin.done"));
      onDone();
      onClose();
    } catch (err) {
      toast.error(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o: boolean) => !o && onClose()}>
      <DialogContent className="rounded-3xl max-w-[calc(100vw-2rem)] sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="account-action-dialog">
        <DialogHeader className="text-start"><DialogTitle className="font-display text-xl">{t(action === "fee" ? "admin.changeFee" : `admin.${action}`)}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {withFee && (
            <div className="space-y-3 rounded-2xl bg-accent/50 p-4">
              <p className="font-bold text-sm">{t("admin.fee")}</p>
              <div className="grid gap-1.5" role="radiogroup" data-testid="fee-model-group">
                {(["per_booking", "per_event", "monthly", "free_trial"] as const).map((m) => (
                  <button type="button" key={m} role="radio" aria-checked={fee.model === m} onClick={() => setFee({ ...fee, model: m })} data-testid={`fee-model-${m}`}
                    className={`rounded-2xl border px-3 py-2 text-start text-sm transition-[background-color,border-color] ${fee.model === m ? "border-2 border-primary bg-primary/15" : "bg-background"} focus:outline-none focus-visible:ring-2 focus-visible:ring-ring`}>
                    <b>{t(`fm.${m}`)}</b><span className="block text-xs text-muted-foreground">{t(`fm.${m}.hint`)}</span>
                  </button>
                ))}
              </div>
              {trial ? (
                <div className="space-y-1.5">
                  <Label htmlFor="fee-trial-days">{t("admin.trialDays")}</Label>
                  <Input id="fee-trial-days" type="number" inputMode="numeric" required min={1} max={365} step={1} dir="ltr" placeholder="30" value={fee.days}
                    onChange={(e: any) => setFee({ ...fee, days: e.target.value })} data-testid="fee-trial-days" className="h-11 rounded-2xl" />
                  {Number(fee.days) >= 1 && <p className="text-xs text-muted-foreground" data-testid="fee-trial-end-hint">{t("admin.trialUntil").replace("{date}", fmtDay(addDays(today(), Number(fee.days)), lang))}</p>}
                </div>
              ) : (
              <div className="flex gap-2">
                <Input dir="ltr" inputMode="decimal" required placeholder={t("admin.feeAmount")} value={fee.amount} onChange={(e: any) => setFee({ ...fee, amount: e.target.value })} data-testid="fee-amount-input" className="h-11 rounded-full" />
                <select value={fee.currency} onChange={(e) => setFee({ ...fee, currency: e.target.value })} data-testid="fee-currency-select" className="h-11 rounded-full border bg-background px-3 text-sm font-bold">
                  <option value="IQD">IQD</option>
                  <option value="USD">USD</option>
                </select>
              </div>
              )}
              <p className="text-xs text-muted-foreground">{t("admin.feeHint")}</p>
            </div>
          )}
          {needsReason && (
            <div className="space-y-1">
              <Label htmlFor="act-reason">{t(needsReason ? "admin.reasonRequired" : "admin.reason")}</Label>
              <textarea id="act-reason" rows={3} required={needsReason} minLength={needsReason ? 3 : undefined} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="action-reason-input" className="w-full rounded-2xl border bg-background p-3 text-sm" />
            </div>
          )}
          <button disabled={busy} data-testid="action-submit-button" className="w-full h-12 rounded-full bg-primary text-primary-foreground font-bold disabled:opacity-60">{busy ? t("common.loading") : t("common.save")}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
